// WS listeners do inbox. Recebe actions despachadas pelo wsMiddleware com prefixo
// `inbox/<event>` e converte em:
//   - patches no cache do RTK Query (mensagens, conversation, lista)
//   - dispatches no slice (ai suggestion, typing TTL)
//
// Setup: registrado uma única vez no AppProviders / store boot via attachInboxWsListeners(store).
// Não toca DOM — puro state. Os componentes consomem via hooks normais.
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { throttle, type Throttled } from '@/lib/utils/throttle';
import { quickRepliesTag } from '@/features/quick-replies';
import {
  inboxApi,
  type ConversationStatus,
  type InboxMessage,
  type MessageReaction,
} from './api';
import {
  aiSuggestionReceived,
  batchingCleared,
  batchingReceived,
  typingExpired,
  typingReceived,
} from './slice';

// listenerMiddleware genérico expõe Dispatch<UnknownAction> que não aceita
// ThunkActions tipados de createApi sem ciclo de tipos. O cast pra
// `(...args: unknown[]) => unknown` é o pragmatic escape — runtime é
// idêntico; tipo da action é validado pelo próprio inboxApi.util.*.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;
function looseDispatch(api: { dispatch: unknown }): LooseDispatch {
  return api.dispatch as LooseDispatch;
}

interface MessageReceivedPayload {
  conversationId: string;
  message: InboxMessage;
}

interface MessageStatusUpdatedPayload {
  conversationId: string;
  messageId: string;
  status: InboxMessage['status'];
}

/**
 * Mensagem mudada depois de gravada: o texto do STT, ou o arquivo que a fila de mídia
 * baixou depois (o aviso "baixando o arquivo…" vira o anexo). Os campos de mídia só vêm
 * quando mudaram, e a `mediaUrl` já vem assinada.
 */
interface MessageUpdatedPayload {
  conversationId: string;
  storeId: string;
  messageId: string;
  text: string | null;
  type?: InboxMessage['type'];
  mediaUrl?: string;
  metadata?: InboxMessage['metadata'];
}

/** Reação trocada ou removida (cliente ou loja). Vem a lista inteira da mensagem. */
interface MessageReactionsUpdatedPayload {
  conversationId: string;
  storeId: string;
  messageId: string;
  reactions: MessageReaction[];
}

interface QuickRepliesChangedPayload {
  storeId: string;
}

interface ConversationWindowClosedPayload {
  conversationId: string;
  /** RF-13: true = janela venceu sem NENHUMA resposta → conversa "Não atendida". */
  unanswered?: boolean;
  /** ISO do carimbo "Não atendido" (momento do sweep) ou null quando houve resposta. */
  unansweredAt?: string | null;
}

interface ConversationClaimedPayload {
  conversationId: string;
  assignedTo: { id: string; name: string } | null;
}

interface ConversationStatusChangedPayload {
  conversationId: string;
  status: ConversationStatus;
  previousStatus: ConversationStatus;
}

interface AutomationToggledPayload {
  conversationId: string;
  enabled: boolean;
  toggledBy: string | null;
}

interface AiTypingPayload {
  conversationId: string;
}

interface AiSuggestionPayload {
  conversationId: string;
  text: string;
}

interface AiBatchingPayload {
  conversationId: string;
  active: boolean;
}

const TYPING_TTL_MS = 5_000;
// Backstop caso o `active:false` (fim do lote) se perca: a janela de agrupamento vai
// no máx. a ~10 min (teto), então limpamos sozinhos um pouco depois disso.
const BATCHING_TTL_MS = 11 * 60_000;

// Janela de coalescing das invalidações de `Conversation/LIST`.
//
// Sem isto cada `message.received` refetchava a lista na hora. O problema não é o custo
// de UMA request: o backend invalida o cache da fila da empresa inteira a cada mensagem
// gravada, e a chave desse cache é POR USUÁRIO para VENDEDOR/ATENDENTE — então N
// atendentes conectados × M mensagens vira N×M execuções da query pesada no Postgres.
// Atendente e volume de IA deixam de somar e passam a multiplicar.
//
// Throttle (leading+trailing) e não debounce: o primeiro evento da rajada continua
// refetchando imediatamente — nada de latência added na UI — e o resto da rajada
// colapsa numa única request no fim da janela.
const LIST_INVALIDATE_MS = 2_000;
// O card "Custo de IA" é informativo e não precisa de latência zero; janela maior.
const COST_INVALIDATE_MS = 10_000;

function isType<T = unknown>(
  action: AnyAction,
  type: string,
): action is AnyAction & { payload: T } {
  return action.type === type;
}

/**
 * Registra todos os listeners do namespace /inbox. Idempotente em testes —
 * podemos chamar várias vezes que o listenerMiddleware deduplica por effect.
 */
export function attachInboxWsListeners(): void {
  // Os throttles vivem no escopo do attach (não do módulo) de propósito: os testes
  // chamam clearListeners() + reattach, e um timer em escopo de módulo sobreviveria
  // entre casos. Construção lazy porque o `dispatch` só existe quando o primeiro
  // evento chega — ele é estável, então capturá-lo uma vez basta.
  let invalidateListThrottled: Throttled | null = null;
  /** Invalida `Conversation/LIST` no máximo 1× por janela, compartilhado por TODOS os
   *  eventos que mexem na lista — throttles separados por evento não coalesceriam
   *  entre si e a rajada voltaria a passar. */
  const invalidateConversationList = (d: LooseDispatch): void => {
    invalidateListThrottled ??= throttle(() => {
      d(inboxApi.util.invalidateTags([{ type: 'Conversation', id: 'LIST' }]));
    }, LIST_INVALIDATE_MS);
    invalidateListThrottled();
  };

  // Custo é por conversa, então acumulamos os ids da janela e invalidamos todos de uma
  // vez — assim uma rajada em N conversas distintas também vira uma request só.
  const pendingCostIds = new Set<string>();
  let flushCostThrottled: Throttled | null = null;
  const invalidateConversationCost = (d: LooseDispatch, conversationId: string): void => {
    pendingCostIds.add(conversationId);
    flushCostThrottled ??= throttle(() => {
      if (pendingCostIds.size === 0) return;
      const tags = [...pendingCostIds].map((id) => ({ type: 'ConversationCost' as const, id }));
      pendingCostIds.clear();
      d(inboxApi.util.invalidateTags(tags));
    }, COST_INVALIDATE_MS);
    flushCostThrottled();
  };

  // message.received → push no cache + invalidate lista pra atualizar preview/unread.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.received',
    effect: (action, listenerApi) => {
      if (!isType<MessageReceivedPayload>(action as AnyAction, 'inbox/message.received')) return;
      const { conversationId, message } = (
        action as AnyAction & { payload: MessageReceivedPayload }
      ).payload;
      const d = looseDispatch(listenerApi);
      d(
        inboxApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
          // Evita duplicação se a mensagem já entrou via optimistic.
          const exists =
            draft.items.some((m) => m.id === message.id) ||
            (message.clientMessageId &&
              draft.items.some((m) => m.clientMessageId === message.clientMessageId));
          if (exists) {
            const idx = draft.items.findIndex(
              (m) =>
                m.id === message.id ||
                (m.clientMessageId && m.clientMessageId === message.clientMessageId),
            );
            if (idx >= 0) draft.items[idx] = message;
          } else {
            draft.items.push(message);
          }
        }),
      );
      // RF-07/RF-11: inbound do cliente reabre a janela de 24h e desfaz o "Não
      // atendido" — espelha no detail sem refetch (badges/composer atualizam ao vivo).
      if (message.direction === 'INBOUND') {
        d(
          inboxApi.util.updateQueryData('getConversation', conversationId, (draft) => {
            if (!draft) return;
            const at = Date.parse(message.createdAt);
            if (!Number.isNaN(at)) {
              draft.windowClosesAt = new Date(at + 24 * 60 * 60 * 1000).toISOString();
            }
            draft.unansweredAt = null;
          }),
        );
      }
      invalidateConversationList(d);
      // RF-05: qualquer mensagem nova pode ser a resposta da IA (LlmUsageLog já gravado
      // antes da entrega) — refetcha o card "Custo de IA" da conversa sem refresh.
      invalidateConversationCost(d, conversationId);
    },
  });

  // message.status.updated → atualiza só o status.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.status.updated',
    effect: (action, listenerApi) => {
      if (!isType<MessageStatusUpdatedPayload>(action as AnyAction, 'inbox/message.status.updated'))
        return;
      const { conversationId, messageId, status } = (
        action as AnyAction & {
          payload: MessageStatusUpdatedPayload;
        }
      ).payload;
      looseDispatch(listenerApi)(
        inboxApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
          const m = draft.items.find((it) => it.id === messageId);
          if (m) m.status = status;
        }),
      );
    },
  });

  // message.updated → atualiza uma mensagem já no cache: o texto (transcrição assíncrona
  // de áudio) ou o arquivo que chegou depois (retentativa do download). Se a mensagem não
  // está no cache, no-op — nada a exibir e evitamos refetch agressivo.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.updated',
    effect: (action, listenerApi) => {
      if (!isType<MessageUpdatedPayload>(action as AnyAction, 'inbox/message.updated')) return;
      const { conversationId, messageId, text, type, mediaUrl, metadata } = (
        action as AnyAction & {
          payload: MessageUpdatedPayload;
        }
      ).payload;
      looseDispatch(listenerApi)(
        inboxApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
          const m = draft.items.find((it) => it.id === messageId);
          if (!m) return;
          m.text = text;
          if (type) m.type = type;
          if (mediaUrl) m.mediaUrl = mediaUrl;
          if (metadata) m.metadata = metadata;
        }),
      );
    },
  });

  // message.reactions.updated → troca as reações da mensagem no cache. Fora do cache
  // (página ainda não carregada), no-op: a mensagem virá com as reações quando carregar.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.reactions.updated',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: MessageReactionsUpdatedPayload }).payload;
      if (!payload?.conversationId || !payload.messageId) return;
      looseDispatch(listenerApi)(
        inboxApi.util.updateQueryData(
          'listMessages',
          { conversationId: payload.conversationId },
          (draft) => {
            const m = draft.items.find((it) => it.id === payload.messageId);
            if (m) m.reactions = payload.reactions ?? [];
          },
        ),
      );
    },
  });

  // quick_replies.changed → a loja mexeu nas respostas rápidas ou nas variáveis: invalida
  // a tag da loja e quem estiver com o menu "/" (ou o card de configuração) recarrega.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/quick_replies.changed',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: QuickRepliesChangedPayload }).payload;
      if (!payload?.storeId) return;
      looseDispatch(listenerApi)(inboxApi.util.invalidateTags([quickRepliesTag(payload.storeId)]));
    },
  });

  // conversation.window.closed → marca windowClosesAt no detalhe. RF-13: o payload
  // pode trazer o carimbo "Não atendido" — espelha e invalida a lista (badge ao vivo).
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/conversation.window.closed',
    effect: (action, listenerApi) => {
      const payload = (
        action as AnyAction & {
          payload: ConversationWindowClosedPayload;
        }
      ).payload;
      if (!payload?.conversationId) return;
      const d = looseDispatch(listenerApi);
      d(
        inboxApi.util.updateQueryData('getConversation', payload.conversationId, (draft) => {
          if (!draft) return;
          draft.windowClosesAt = new Date().toISOString();
          if (payload.unanswered) {
            draft.unansweredAt = payload.unansweredAt ?? new Date().toISOString();
          }
        }),
      );
      invalidateConversationList(d);
    },
  });

  // conversation.claimed / released / assigned → invalida lista + detail.
  listenerMiddleware.startListening({
    predicate: (action) => {
      const t = (action as AnyAction).type;
      return (
        t === 'inbox/conversation.claimed' ||
        t === 'inbox/conversation.released' ||
        t === 'inbox/conversation.assigned'
      );
    },
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: ConversationClaimedPayload }).payload;
      if (!payload?.conversationId) return;
      const d = looseDispatch(listenerApi);
      // O detalhe da conversa continua imediato: quem clicou em "assumir" precisa ver
      // o efeito na hora. Só a LISTA entra na janela compartilhada.
      d(inboxApi.util.invalidateTags([{ type: 'Conversation', id: payload.conversationId }]));
      invalidateConversationList(d);
    },
  });

  // conversation.status.changed → atualiza status no detalhe + invalida lista.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/conversation.status.changed',
    effect: (action, listenerApi) => {
      const payload = (
        action as AnyAction & {
          payload: ConversationStatusChangedPayload;
        }
      ).payload;
      if (!payload?.conversationId) return;
      const d = looseDispatch(listenerApi);
      d(
        inboxApi.util.updateQueryData('getConversation', payload.conversationId, (draft) => {
          if (draft) draft.status = payload.status;
        }),
      );
      invalidateConversationList(d);
    },
  });

  // automation.toggled → atualiza flag em detail + invalida list.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/automation.toggled',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload: AutomationToggledPayload }).payload;
      if (!payload?.conversationId) return;
      looseDispatch(listenerApi)(
        inboxApi.util.updateQueryData('getConversation', payload.conversationId, (draft) => {
          if (draft?.automation) draft.automation.enabled = payload.enabled;
        }),
      );
    },
  });

  // ai.typing → seta typing=true, agenda expiração após TTL.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/ai.typing',
    effect: async (action, listenerApi) => {
      const payload = (action as AnyAction & { payload: AiTypingPayload }).payload;
      if (!payload?.conversationId) return;
      const { conversationId } = payload;
      // IA começou a digitar → não está mais só agrupando: limpa o indicador de espera.
      listenerApi.dispatch(batchingCleared({ conversationId }));
      listenerApi.dispatch(typingReceived({ conversationId }));
      listenerApi.cancelActiveListeners();
      await listenerApi.delay(TYPING_TTL_MS);
      listenerApi.dispatch(typingExpired({ conversationId }));
    },
  });

  // RF-02: ai.batching → janela de agrupamento aberta (active) / fechada. Mostra o
  // indicador "IA aguardando mais mensagens…" com backstop de TTL caso o fim se perca.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/ai.batching',
    effect: async (action, listenerApi) => {
      const payload = (action as AnyAction & { payload: AiBatchingPayload }).payload;
      if (!payload?.conversationId) return;
      const { conversationId, active } = payload;
      listenerApi.cancelActiveListeners();
      if (!active) {
        listenerApi.dispatch(batchingCleared({ conversationId }));
        return;
      }
      listenerApi.dispatch(batchingReceived({ conversationId }));
      await listenerApi.delay(BATCHING_TTL_MS);
      listenerApi.dispatch(batchingCleared({ conversationId }));
    },
  });

  // ai.suggestion → grava no slice.
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/ai.suggestion',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload: AiSuggestionPayload }).payload;
      if (!payload?.conversationId || typeof payload.text !== 'string') return;
      listenerApi.dispatch(aiSuggestionReceived(payload));
    },
  });
}
